import { ChangeEvent, useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import AddAPhoto from "@mui/icons-material/AddAPhoto";

import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Fab from "@mui/material/Fab";
import Badge from "@mui/material/Badge";
import Paper from "@mui/material/Paper";
import { useAppDispatch } from "../../../app/hooks";
import Box from "@mui/material/Box";
import Toast from "../../../components/Layout/Toast";

import AuthSkeleton from "../../auth/AuthSkeleton";
import { resizeFile } from "../../../utils/imageUtility";
import { uploadCompanyPicture } from "../setupSlices";
import { PhotoTypes } from "../../process/types/processTypes";
import { Company } from "../types/setupTypes";
import { apolloClient } from "../../../apollo/graphql";
import { GET_SELECTED_COMPANY } from "../../../apollo/queries";
import { Typography } from "@mui/material";
import { ClientTypes } from "../../auth/types/authType";

interface CompanyProps {
  companyId: string;
  type: ClientTypes;
}

function CompanyUploads({ companyId, type }: CompanyProps) {
  const [stampImage, setStampImage] = useState("");
  const [signatureImage, setSignatureImage] = useState("");
  const [empSignatureImage, setEmpSignatureImage] = useState("");
  const [currentCompany, setCurrentCompany] = useState<Company | undefined>();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const empType = type === ClientTypes.LocalAgency ? " Employee" : " Employer";

  // Initial fetch when component mounts
  useEffect(() => {
    if (companyId) {
      const fetchCompanyData = async () => {
        try {
          const response = await apolloClient.query({
            query: GET_SELECTED_COMPANY,
            variables: { id: companyId },
          });

          if (response && response.data && response.data.getCompany) {
            setCurrentCompany(response.data.getCompany as Company);
          } else {
            console.log(
              `CompanyUploads: No company data found for ID: ${companyId}`
            );
          }
        } catch (error) {
          console.error(
            `CompanyUploads: Error fetching company for ID ${companyId}:`,
            error
          );
        }
      };

      fetchCompanyData();
    }
  }, [companyId]);

  // Handle successful uploads - refetch when this component's upload succeeds
  // useEffect(() => {
  //   if (localSuccess && uploadSuccess) {
  //     console.log(
  //       `CompanyUploads: Refetching data after successful upload for company: ${companyId}`
  //     );
  //     const refetchCompanyData = async () => {
  //       try {
  //         const response = await apolloClient.query({
  //           query: GET_SELECTED_COMPANY,
  //           variables: { id: companyId },
  //           fetchPolicy: "network-only", // Force fresh data from server
  //         });

  //         if (response && response.data && response.data.getCompany) {
  //           console.log(
  //             `CompanyUploads: Successfully refetched company data:`,
  //             response.data.getCompany
  //           );
  //           setCurrentCompany(response.data.getCompany as Company);
  //           setUploadSuccess(false); // Reset the flag
  //           setLocalSuccess(null); // Clear success message
  //         }
  //       } catch (error) {
  //         console.error(
  //           `CompanyUploads: Error refetching company after upload for ${companyId}:`,
  //           error
  //         );
  //         setUploadSuccess(false); // Reset the flag even on error
  //         setLocalError("Failed to refresh company data");
  //       }
  //     };

  //     refetchCompanyData();
  //   }
  // }, [localSuccess, uploadSuccess, companyId]);

  const handleUpload = async (
    image: File,
    type: PhotoTypes,
    companyName: string
  ) => {
    if (!currentCompany) return;

    setLocalLoading(true);
    setLocalError(null);
    setLocalSuccess(null);

    try {
      const result = await dispatch(
        uploadCompanyPicture({
          _id: currentCompany._id as string,
          type: type,
          avatar: image,
        })
      ).unwrap();

      setLocalSuccess(
        `${currentCompany.displayName} ${type} uploaded successfully`
      );
      setUploadSuccess(true);
    } catch (error: any) {
      setLocalError(error.message || "Upload failed");
    } finally {
      setLocalLoading(false);
    }
  };

  const onChangeCompanyStamp = async (event: ChangeEvent<HTMLInputElement>) => {
    const { validity, files } = event.target;

    if (validity.valid && files && files[0]) {
      setStampImage(URL.createObjectURL(files[0]) as string);
      const image = (await resizeFile(files[0], 150, 150)) as File;
      if (image && currentCompany) {
        await handleUpload(
          image,
          PhotoTypes.CompanyStamp,
          currentCompany.displayName
        );
      }
    }
  };
  const onChangeCompanySignature = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { validity, files } = event.target;

    if (validity.valid && files && files[0]) {
      setSignatureImage(URL.createObjectURL(files[0]) as string);
      const image = (await resizeFile(files[0], 150, 150)) as File;
      if (image && currentCompany) {
        await handleUpload(
          image,
          PhotoTypes.CompanySignature,
          currentCompany.displayName
        );
      }
    }
  };
  const onChangeEmpSignature = async (event: ChangeEvent<HTMLInputElement>) => {
    const { validity, files } = event.target;

    if (validity.valid && files && files[0]) {
      setEmpSignatureImage(URL.createObjectURL(files[0]) as string);
      const image = (await resizeFile(files[0], 150, 150)) as File;
      if (image && currentCompany) {
        await handleUpload(
          image,
          PhotoTypes.EmpSignature,
          currentCompany.displayName
        );
      }
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        style={{
          position: "relative",
          borderRadius: 18,
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          alignItems: "center",
          width: "100%",
          gap: 10,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          {currentCompany && currentCompany._id && (
            <Typography
              variant="body1"
              sx={{
                color: "#6E6E6E",
                fontSize: "1.1rem",
                fontWeight: "400",
                lineHeight: "1.43em",
              }}
            >
              {" Stamp"}
            </Typography>
          )}
          <input
            name={`${companyId}_companyStampImage`}
            accept="image/*"
            onChange={onChangeCompanyStamp}
            style={{ display: "none" }}
            id={`${companyId}_icon-button-file-company-stamp`}
            type="file"
          />
          <label htmlFor={`${companyId}_icon-button-file-company-stamp`}>
            <>
              <Badge
                overlap="rectangular"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                badgeContent={
                  <Tooltip
                    title="Add/Change Company Stamp"
                    aria-label={`${companyId}_icon-button-file-company-stamp`}
                  >
                    <Fab component="span" color="primary">
                      <IconButton
                        aria-label={`${companyId}_icon-button-file-company-stamp`}
                        component="span"
                        size="small"
                        color="inherit"
                      >
                        <AddAPhoto />
                      </IconButton>
                    </Fab>
                  </Tooltip>
                }
              >
                {localLoading ? (
                  <AuthSkeleton />
                ) : (
                  <Avatar
                    alt="avatar"
                    variant="rounded"
                    src={stampImage || currentCompany?.companyStamp || ""}
                    sx={{
                      height: 150,
                      width: 150,
                    }}
                  />
                )}
              </Badge>
            </>
          </label>{" "}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          {currentCompany && currentCompany._id && (
            <Typography
              variant="body1"
              sx={{
                color: "#6E6E6E",
                fontSize: "1.1rem",
                fontWeight: "400",
                lineHeight: "1.43em",
              }}
            >
              {" Signature"}
            </Typography>
          )}
          <input
            name={`${companyId}_companySignatureImage`}
            accept="image/*"
            onChange={onChangeCompanySignature}
            style={{ display: "none" }}
            id={`${companyId}_icon-button-file-company-signature`}
            type="file"
          />
          <label htmlFor={`${companyId}_icon-button-file-company-signature`}>
            <>
              <Badge
                overlap="rectangular"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                badgeContent={
                  <Tooltip
                    title="Add/Change Company Signature"
                    aria-label={`${companyId}_icon-button-file-company-signature`}
                  >
                    <Fab component="span" color="primary">
                      <IconButton
                        aria-label={`${companyId}_icon-button-file-company-signature`}
                        component="span"
                        size="small"
                        color="inherit"
                      >
                        <AddAPhoto />
                      </IconButton>
                    </Fab>
                  </Tooltip>
                }
              >
                {localLoading ? (
                  <AuthSkeleton />
                ) : (
                  <Avatar
                    alt="avatar"
                    variant="rounded"
                    src={
                      signatureImage || currentCompany?.companySignature || ""
                    }
                    sx={{
                      height: 150,
                      width: 150,
                    }}
                  />
                )}
              </Badge>
            </>
          </label>{" "}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          {currentCompany && currentCompany._id && (
            <Typography
              variant="body1"
              sx={{
                color: "#6E6E6E",
                fontSize: "1.1rem",
                fontWeight: "400",
                lineHeight: "1.43em",
              }}
            >
              {empType + " Signature"}
            </Typography>
          )}
          <input
            name={`${companyId}_empSignatureImage`}
            accept="image/*"
            onChange={onChangeEmpSignature}
            style={{ display: "none" }}
            id={`${companyId}_icon-button-file-emp-signature`}
            type="file"
          />
          <label htmlFor={`${companyId}_icon-button-file-emp-signature`}>
            <>
              <Badge
                overlap="rectangular"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                badgeContent={
                  <Tooltip
                    title="Add/Change Employee/r Signature"
                    aria-label={`${companyId}_icon-button-file-emp-signature`}
                  >
                    <Fab component="span" color="primary">
                      <IconButton
                        aria-label={`${companyId}_icon-button-file-emp-signature`}
                        component="span"
                        size="small"
                        color="inherit"
                      >
                        <AddAPhoto />
                      </IconButton>
                    </Fab>
                  </Tooltip>
                }
              >
                {localLoading ? (
                  <AuthSkeleton />
                ) : (
                  <Avatar
                    alt="avatar"
                    variant="rounded"
                    src={
                      empSignatureImage || currentCompany?.empSignature || ""
                    }
                    sx={{
                      height: 150,
                      width: 150,
                    }}
                  />
                )}
              </Badge>
            </>
          </label>{" "}
        </Box>
        <Box m={1}>
          {localError && <Toast severity="error">{localError}</Toast>}
          {localSuccess && <Toast severity="success">{localSuccess}</Toast>}
        </Box>
      </Paper>
    </>
  );
}

export default CompanyUploads;
